import { BaseProjectIdProps } from "@custom-types/sdb-company-types";
import { useProjectIntegrations } from "@pages/project-details/project-integrations/use-project-integrations";
import { NoIntegration } from "@pages/integrations/no-integration";
import { IntegrationSection } from "@pages/integrations/integration-section";
import {
  IntegrationSections,
  ProjectConnectionDialogType,
} from "@pages/integrations/integrations-types";
import { ProjectIntegrationsDialog } from "@pages/project-details/project-integrations/project-integrations-dialog";
import { useState } from "react";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@hooks/use-toast";
import { IntegrationProject } from "@services/integrations-service/integrations-service-types";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { IntegrationSkeleton } from "@components/common/skeleton-loading/integration-skeleton";
import { IntegrationDialog } from "@pages/integrations/integrations-dialog/integration-dialog";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import FailedIcon from "@assets/icons/failed_32px.svg?react";
import { FaroButton } from "@components/common/faro-button";

/** Renders project integration page */
export function ProjectIntegrations({
  projectId,
}: BaseProjectIdProps): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();

  const [selectedIntegrationId, setSelectedIntegrationId] =
    useState<SphereDashboardAPITypes.IntegrationId>(
      SphereDashboardAPITypes.IntegrationId.autodesk
    );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shouldShowConnectedDialog, setShouldShowConnectedDialog] =
    useState<boolean>(false);

  const {
    integrationsService,
    connectProject,
    disconnectProject,
    enabledWorkspaceIntegrations,
    enabledProjectIntegrations,
    getProjectIntegrationName,
    isLoadingIntegrations,
    selectedDialog,
    cancelReauthorization,
    projectDialogType,
    setProjectDialogType,
    shouldShowErrorPage,
  } = useProjectIntegrations({
    projectId,
  });

  /** Integrations that are not connected to the project */
  const availableIntegrations = enabledWorkspaceIntegrations.filter(
    (integration) => !enabledProjectIntegrations.includes(integration)
  );

  function onIntegrationActionClicked(
    integrationId: SphereDashboardAPITypes.IntegrationId,
    dialogType: ProjectConnectionDialogType
  ): void {
    setSelectedIntegrationId(integrationId);
    setProjectDialogType(dialogType);
  }

  async function onConnectConfirm(
    selectedIntegrationProject: IntegrationProject
  ): Promise<void> {
    trackEvent({
      name: ProjectEvents.connectIntegration,
      props: { integrationId: selectedIntegrationId },
    });

    try {
      setIsLoading(true);
      await connectProject(selectedIntegrationProject);

      setProjectDialogType("none");
      setShouldShowConnectedDialog(true);
    } catch (error) {
      handleErrorWithToast({
        id: `connectProject-${Date.now().toString()}`,
        title: `Error connecting project to ${IntegrationSections[selectedIntegrationId].displayName}`,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function onDisconnectConfirm(): Promise<void> {
    trackEvent({
      name: ProjectEvents.disconnectIntegration,
      props: { integrationId: selectedIntegrationId },
    });

    try {
      setIsLoading(true);
      await disconnectProject(selectedIntegrationId);
      setProjectDialogType("none");

      showToast({
        message: `${IntegrationSections[selectedIntegrationId].displayName} is disconnected from this project`,
        type: "success",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `disconnectProject-${Date.now().toString()}`,
        title: `Error disconnecting project to ${IntegrationSections[selectedIntegrationId].displayName}`,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoadingIntegrations) {
    return <IntegrationSkeleton />;
  }

  if (shouldShowErrorPage) {
    return (
      <EmptyPage
        title="Something went wrong"
        subtitle="Failed to load the project integrations data. Please refresh the page and try again."
        icon={FailedIcon}
        button={
          <FaroButton
            sx={{ my: "85px" }}
            onClick={() => window.location.reload()}
          >
            Refresh
          </FaroButton>
        }
      />
    );
  }

  return (
    <>
      {enabledProjectIntegrations.length === 0 ? (
        <NoIntegration subject="project" />
      ) : (
        <IntegrationSection
          title="Connected integrations"
          integrations={enabledProjectIntegrations}
          isConnected={true}
          enabledWorkspaceIntegrations={enabledWorkspaceIntegrations}
          actionButtonText="DisconnectFromProject"
          onIntegrationAction={(integrationId) =>
            onIntegrationActionClicked(integrationId, "disconnect")
          }
          getProjectIntegrationName={getProjectIntegrationName}
        />
      )}

      {availableIntegrations.length !== 0 && (
        <IntegrationSection
          title="Disconnected integrations"
          integrations={availableIntegrations}
          actionButtonText="ConnectToProject"
          onIntegrationAction={(integrationId) =>
            onIntegrationActionClicked(integrationId, "connect")
          }
        />
      )}

      <ProjectIntegrationsDialog
        integrationsService={integrationsService}
        selectedIntegrationId={selectedIntegrationId}
        onConnectConfirm={onConnectConfirm}
        isLoading={isLoading}
        shouldShowConnectedDialog={shouldShowConnectedDialog}
        setShouldShowConnectedDialog={setShouldShowConnectedDialog}
        onDisconnectConfirm={onDisconnectConfirm}
        dialogType={projectDialogType}
        onClose={() => setProjectDialogType("none")}
      />

      {selectedDialog && (
        <IntegrationDialog
          selectedIntegrationId={selectedIntegrationId}
          selectedDialog={selectedDialog}
          // The "Success" dialog is not used, we can pass a void function
          onSuccessDialogConfirm={() => undefined}
          onInProgressDialogCancel={cancelReauthorization}
          // The "Disconnect" dialog is not used, we can pass a void function
          onDisconnectDialogConfirm={() => undefined}
          // The "Disconnect" dialog is not used, we can pass a void function
          onDisconnectDialogCancel={() => undefined}
        />
      )}
    </>
  );
}
